import { useInfiniteQuery } from '@tanstack/react-query'
import { Loading } from 'react-daisyui'
import { IconPhoto } from '@tabler/icons-react'
import { Button } from '@/components/Button.tsx'
import { PaginatedResponse, useClient } from '@/stores/useClient.ts'
import { StatFilters, StatHistoryEntry } from '@/models/stats.ts'

const PAGE_SIZE = 25

const PLATFORM_LABELS: Record<string, string> = {
  ios: 'iPhone / iPad',
  android: 'Android',
  web: 'Web',
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

/** The meta line: only what this particular event actually carries. */
function buildDetails(entry: StatHistoryEntry): string {
  const parts = [formatDateTime(entry.created_at)]
  if (entry.file_name) parts.push(entry.file_name)
  if (entry.target) parts.push(entry.target)
  if (entry.albums_count != null) parts.push(`${entry.albums_count} ${entry.albums_count === 1 ? 'Album' : 'Alben'}`)
  if (entry.files_count != null) parts.push(`${entry.files_count} ${entry.files_count === 1 ? 'Bild' : 'Bilder'}`)
  if (entry.platform && PLATFORM_LABELS[entry.platform]) parts.push(PLATFORM_LABELS[entry.platform])
  return parts.join(' · ')
}

interface StatHistoryListProps {
  filters?: StatFilters
  /** Shown above the list; omit inside a section that already has a heading. */
  title?: string
  /** Show which gallery an entry belongs to (unfiltered views). */
  showDriveTitle?: boolean
}

export default function StatHistoryList({ filters = {}, title = 'Verlauf', showDriveTitle }: StatHistoryListProps) {
  const { getStatsHistory } = useClient()
  const filterKey = JSON.stringify(filters)

  const query = useInfiniteQuery({
    queryKey: ['stats', 'history', filterKey],
    queryFn: async ({ pageParam }) => {
      const res = await getStatsHistory({ ...filters, page: pageParam, page_size: PAGE_SIZE })
      return res.data as PaginatedResponse<StatHistoryEntry>
    },
    getNextPageParam: (lastPage, pages) => (lastPage.next ? pages.length + 1 : undefined),
    initialPageParam: 1,
  })

  const entries = query.data?.pages.flatMap(page => page.results) ?? []

  return (
    <div className='bg-neutral border border-[var(--portal-border)] p-4 md:p-6'>
      {title && <h3 className='font-serif text-lg mb-4'>{title}</h3>}

      {query.isLoading ? (
        <div className='flex justify-center py-6'><Loading color='primary' /></div>
      ) : entries.length === 0 ? (
        <p className='text-sm opacity-50 py-4'>Noch keine Aktivitäten</p>
      ) : (
        <div className='flex flex-col'>
          {entries.map(entry => (
            <div key={entry.id} className='flex items-center gap-3 py-2 border-t border-[var(--portal-border)] first:border-t-0'>
              {entry.file?.thumbnail ? (
                <img src={entry.file.thumbnail} alt='' className='w-10 h-10 rounded-lg object-cover flex-shrink-0' />
              ) : (
                <div className='w-10 h-10 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0'>
                  <IconPhoto size={18} className='opacity-30' />
                </div>
              )}
              <div className='min-w-0 flex-1'>
                <div className='text-sm truncate'>{entry.label}</div>
                <div className='text-xs opacity-50 truncate'>{buildDetails(entry)}</div>
              </div>
              {showDriveTitle && entry.drive_title && (
                <div className='text-xs opacity-50 hidden md:block flex-shrink-0 max-w-[12rem] truncate'>
                  {entry.drive_title}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {query.hasNextPage && (
        <div className='mt-4'>
          <Button
            size='sm'
            variant='outline'
            loading={query.isFetchingNextPage}
            disabled={query.isFetchingNextPage}
            onClick={() => query.fetchNextPage()}
          >
            Mehr laden
          </Button>
        </div>
      )}
    </div>
  )
}
